import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { MARK_SVG } from '../data/assets';
type Variant = {
  id: string;
  label: string;
  note: string;
  background: string;
  /** Mark inset as a share of the tile, so it holds at any export size. */
  padding: number;
};

/**
 * A favicon is read at 16px in a browser tab, so it can only carry the mark —
 * the wordmark would be illegible. Two treatments, because a transparent mark
 * vanishes against a dark tab strip: a white tile reads on every theme, a blue
 * tile reads as brand. Both are square with generous padding, since browsers
 * and phone home screens crop the corners differently.
 */
const VARIANTS: Variant[] = [{
  id: 'light',
  label: 'White tile',
  note: 'Safest — holds on light and dark tab strips alike',
  background: '#FFFFFF',
  padding: 0.18
}, {
  id: 'blue',
  label: 'Blue tile',
  note: 'Brand-forward — more visible in a crowded tab strip',
  background: '#0E6FA3',
  padding: 0.2
}];
const SIZE = 512;
export function FaviconCard() {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  async function download(id: string) {
    const node = refs.current[id];
    if (!node) return;
    setState('working');
    try {
      const url = await toPng(node, {
        width: SIZE,
        height: SIZE,
        pixelRatio: 1,
        cacheBust: true
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = 'favicon.png';
      a.click();
      setState('done');
    } catch {
      setState('error');
    }
  }
  return <div style={{
    minHeight: '100vh',
    background: '#E9EDF1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 36,
    padding: 40
  }} data-id="element-419" data-mp="m1xc8cmp742q">
      <div style={{
      display: 'flex',
      gap: 48,
      flexWrap: 'wrap',
      justifyContent: 'center'
    }} data-id="element-420" data-mp="1of9rflx3xb68">
        {VARIANTS.map(variant => <div key={variant.id} style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18
      }} data-id="element-421" data-mp="1jh8c9c2sxw01">
            {/* Rendered at full export size, scaled down for display only, so
                what you download is exactly what you see. */}
            <div style={{
          width: 200,
          height: 200,
          position: 'relative'
        }} data-id="element-422" data-mp="15gnqc71lbk2b">
              <div ref={node => {
            refs.current[variant.id] = node;
          }} style={{
            width: SIZE,
            height: SIZE,
            background: variant.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `scale(${200 / SIZE})`,
            transformOrigin: 'top left',
            border: variant.id === 'light' ? '1px solid #E3E7EC' : 'none',
            boxSizing: 'border-box'
          }} data-id="element-423" data-mp="10pxlqw0znl8o">
                <img src={MARK_SVG} alt="" style={{
              width: `${100 - variant.padding * 200}%`,
              display: 'block',
              filter: variant.id === 'blue' ? 'brightness(0) invert(1)' : 'none'
            }} data-id="element-424" data-mp="2ybyfzoezgtme" />
              </div>
            </div>

            {/* Tab-strip preview at true 16px, which is the only size that
                actually decides whether a favicon works. */}
            <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#F1F3F5',
          border: '1px solid #DDE2E8',
          padding: '7px 12px',
          borderRadius: 6
        }} data-id="element-425" data-mp="2jmsluev05tld">
              <span style={{
            width: 16,
            height: 16,
            flex: 'none',
            background: variant.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }} data-id="element-426" data-mp="1vwofhzqfjfqo">
                <img src={MARK_SVG} alt="" style={{
              width: `${100 - variant.padding * 200}%`,
              display: 'block',
              filter: variant.id === 'blue' ? 'brightness(0) invert(1)' : 'none'
            }} data-id="element-427" data-mp="1hro8cjf25p8n" />
              </span>
              <span style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: '.04em',
            color: 'var(--ink-2)'
          }} data-id="element-428" data-mp="20k0951q2cigo">
                meetsynthia.ai
              </span>
            </div>

            <div style={{
          textAlign: 'center',
          maxWidth: 240
        }} data-id="element-429" data-mp="2hssztw6mw2yh">
              <div style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'var(--ink)'
          }} data-id="element-430" data-mp="2hvf8jz59goia">
                {variant.label}
              </div>
              <p style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--mut)',
            marginTop: 6
          }} data-id="element-431" data-mp="1z2bliwmjt9wn">
                {variant.note}
              </p>
            </div>

            <button type="button" className={variant.id === 'light' ? 'btn g' : 'btn o'} onClick={() => download(variant.id)} disabled={state === 'working'} data-id="element-432" data-mp="231tx31shc8mt">
              {state === 'working' ? 'Preparing\u2026' : 'Download favicon.png'}
            </button>
          </div>)}
      </div>

      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 16
    }} data-id="element-433" data-mp="3ogzne3xij9gf">
        <span style={{
        fontFamily: 'var(--mono)',
        fontSize: 12,
        letterSpacing: '.05em',
        color: state === 'error' ? '#b8492e' : 'var(--ink-2)'
      }} data-id="element-434" data-mp="3n0m35xjj76mz">
          {state === 'done' ? 'Saved to your Downloads \u2014 upload it in Publish \u2192 Site Metadata' : state === 'error' ? 'Couldn\u2019t render \u2014 try once more' : `${SIZE}\u00d7${SIZE} \u00b7 saves to your Downloads folder`}
        </span>
        <Link className="btn o" to="/" data-id="element-435" data-mp="4muzkyj2u4ew">
          &larr; Back to the site
        </Link>
      </div>
    </div>;
}